import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

// Local cache of encrypted (WCV1) media containers and lazily-derived
// encrypted thumbnails. Both stores hold ciphertext only — same bit pattern
// as S3, no extra privacy surface. Caching at this layer means a reload
// skips both the S3 GET *and* its decryption on subsequent visits, and works
// offline once the SW has served the bytes once.

const DB_NAME = 'photo-vault-media';
// v2 (2026-05-02): bumped to wipe v1 thumbs after raising the worker's
// thumbnail resolution/quality. Cipher store untouched so nothing has to be
// re-downloaded from S3 — only the thumb pass re-runs on demand.
const DB_VERSION = 2;
const STORE_CIPHER = 'cipher';
const STORE_THUMB = 'thumb';

interface CipherEntry {
  photoId: string;
  bytes: Uint8Array;
  sizeBytes: number;
  lastAccessedAt: number;
}

interface ThumbEntry {
  photoId: string;
  // The thumb is itself a WCV1 container, persisted as the UTF-8 bytes of the
  // base64url string the worker produced. Decoding once at read time is
  // cheaper than carrying a String here.
  bytes: Uint8Array;
  sizeBytes: number;
  lastAccessedAt: number;
}

interface MediaSchema extends DBSchema {
  [STORE_CIPHER]: {
    key: string;
    value: CipherEntry;
    indexes: { byLastAccessed: number };
  };
  [STORE_THUMB]: {
    key: string;
    value: ThumbEntry;
    indexes: { byLastAccessed: number };
  };
}

let dbPromise: Promise<IDBPDatabase<MediaSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<MediaSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<MediaSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const cipher = db.createObjectStore(STORE_CIPHER, { keyPath: 'photoId' });
          cipher.createIndex('byLastAccessed', 'lastAccessedAt');
        }
        if (oldVersion < 2) {
          // Drop any v1 thumbs (low-res/low-quality) and recreate the store
          // empty — the next visit re-derives them at the new spec.
          if (db.objectStoreNames.contains(STORE_THUMB)) {
            db.deleteObjectStore(STORE_THUMB);
          }
          const thumb = db.createObjectStore(STORE_THUMB, { keyPath: 'photoId' });
          thumb.createIndex('byLastAccessed', 'lastAccessedAt');
        }
      },
    });
  }
  return dbPromise;
}

// Quotas tuned for typical phone storage budgets. The browser may evict
// IndexedDB anyway under storage pressure — these caps just bound our
// growth so we don't fight the platform for the entire quota.
const CIPHER_QUOTA_BYTES = 500 * 1024 * 1024; // 500 MB
const THUMB_QUOTA_BYTES = 100 * 1024 * 1024; // 100 MB

// Touch updates re-issue a `put` (readwrite tx) just to refresh
// lastAccessedAt for LRU. With ~200 tiles mounted on first paint, doing one
// touch per get serializes a queue of readwrite tx ahead of every actual
// read. We only refresh the timestamp when it has gotten meaningfully stale.
const TOUCH_THROTTLE_MS = 5 * 60 * 1000;

// Eviction scans the entire byLastAccessed index inside a readwrite tx, so
// running it after every put created a chain of long readwrite tx that
// blocked all gets behind it under bulk-decrypt load. We coalesce eviction:
// puts mark the store dirty, the timer fires at most once every 30s
// (preferring requestIdleCallback when available) and processes both stores
// if dirty.
const EVICT_DELAY_MS = 30_000;

const dirty: Record<typeof STORE_CIPHER | typeof STORE_THUMB, boolean> = {
  [STORE_CIPHER]: false,
  [STORE_THUMB]: false,
};
let evictTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleEviction(store: typeof STORE_CIPHER | typeof STORE_THUMB): void {
  dirty[store] = true;
  if (evictTimer !== null) return;
  evictTimer = setTimeout(() => {
    evictTimer = null;
    runEvictionPass();
  }, EVICT_DELAY_MS);
}

function runEvictionPass(): void {
  const run = () => {
    if (dirty[STORE_CIPHER]) {
      dirty[STORE_CIPHER] = false;
      void evictIfOverQuota(STORE_CIPHER, CIPHER_QUOTA_BYTES);
    }
    if (dirty[STORE_THUMB]) {
      dirty[STORE_THUMB] = false;
      void evictIfOverQuota(STORE_THUMB, THUMB_QUOTA_BYTES);
    }
  };
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (typeof ric === 'function') ric(run);
  else run();
}

export async function getCipher(photoId: string): Promise<Uint8Array | null> {
  try {
    const db = await getDb();
    const entry = await db.get(STORE_CIPHER, photoId);
    if (!entry) return null;
    maybeTouch(STORE_CIPHER, entry);
    return entry.bytes;
  } catch {
    return null;
  }
}

export async function putCipher(photoId: string, bytes: Uint8Array): Promise<void> {
  try {
    const db = await getDb();
    await db.put(STORE_CIPHER, {
      photoId,
      bytes,
      sizeBytes: bytes.byteLength,
      lastAccessedAt: Date.now(),
    });
    scheduleEviction(STORE_CIPHER);
  } catch {
    // Cache is best-effort; ignore quota errors.
  }
}

export async function getThumb(photoId: string): Promise<Uint8Array | null> {
  try {
    const db = await getDb();
    const entry = await db.get(STORE_THUMB, photoId);
    if (!entry) return null;
    maybeTouch(STORE_THUMB, entry);
    return entry.bytes;
  } catch {
    return null;
  }
}

export async function putThumb(photoId: string, bytes: Uint8Array): Promise<void> {
  try {
    const db = await getDb();
    await db.put(STORE_THUMB, {
      photoId,
      bytes,
      sizeBytes: bytes.byteLength,
      lastAccessedAt: Date.now(),
    });
    scheduleEviction(STORE_THUMB);
  } catch {
    // ignore
  }
}

export async function deleteCachedPhoto(photoId: string): Promise<void> {
  try {
    const db = await getDb();
    await Promise.all([db.delete(STORE_CIPHER, photoId), db.delete(STORE_THUMB, photoId)]);
  } catch {
    // ignore
  }
}

function maybeTouch(
  store: typeof STORE_CIPHER | typeof STORE_THUMB,
  entry: CipherEntry | ThumbEntry,
): void {
  const now = Date.now();
  if (now - entry.lastAccessedAt < TOUCH_THROTTLE_MS) return;
  void (async () => {
    try {
      const db = await getDb();
      await db.put(store, { ...entry, lastAccessedAt: now });
    } catch {
      // touch is best-effort
    }
  })();
}

async function evictIfOverQuota(
  storeName: typeof STORE_CIPHER | typeof STORE_THUMB,
  quotaBytes: number,
): Promise<void> {
  try {
    const db = await getDb();
    // Sum sizes via the byLastAccessed index in ascending order and trim from
    // the oldest end until we're under quota. cursor.continue() walks the
    // index without loading every record into memory.
    const tx = db.transaction(storeName, 'readwrite');
    const index = tx.store.index('byLastAccessed');
    let total = 0;
    const all: { photoId: string; size: number }[] = [];
    let cursor = await index.openCursor();
    while (cursor) {
      const v = cursor.value as CipherEntry | ThumbEntry;
      total += v.sizeBytes;
      all.push({ photoId: v.photoId, size: v.sizeBytes });
      cursor = await cursor.continue();
    }
    if (total <= quotaBytes) {
      await tx.done;
      return;
    }
    let freed = 0;
    const toFree = total - quotaBytes;
    for (const e of all) {
      if (freed >= toFree) break;
      await tx.store.delete(e.photoId);
      freed += e.size;
    }
    await tx.done;
  } catch {
    // ignore
  }
}
